import { useState, useEffect, useCallback } from "react";

/**
 * Hook para carregar o SDK do Facebook dinamicamente com um appId específico
 * Isso permite usar diferentes appIds para Facebook/Instagram e WhatsApp Business
 * O SDK só é carregado quando loadSDK() é chamado explicitamente
 */
export const useFacebookSDK = (appId) => {
  const [sdkLoaded, setSdkLoaded] = useState(false);
  const [loading, setLoading] = useState(false);

  // Função para carregar o SDK - só executa quando chamada explicitamente
  const loadSDK = useCallback(() => {
    if (!appId) {
      console.warn("useFacebookSDK: appId não fornecido");
      return Promise.reject("appId não fornecido");
    }

    // Se o SDK já estiver carregado com este appId, não recarrega
    if (window.FB && window.FB.getAppId && window.FB.getAppId() === appId) {
      setSdkLoaded(true);
      return Promise.resolve();
    }

    setLoading(true);

    return new Promise((resolve, reject) => {
      // Função para inicializar o SDK
      const initializeSDK = () => {
        try {
          if (window.FB) {
            window.FB.init({
              appId: appId,
              autoLogAppEvents: true,
              xfbml: true,
              version: "v22.0",
            });
            setSdkLoaded(true);
            setLoading(false);
            console.log(`✅ Facebook SDK inicializado com appId: ${appId}`);
            resolve();
          }
        } catch (err) {
          console.error("❌ Erro ao inicializar Facebook SDK:", err);
          setLoading(false);
          reject(err);
        }
      };

      // Verificar se o script já foi carregado
      if (window.FB) {
        // Se o SDK já existe mas com appId diferente, precisamos recarregar
        // Remover o script existente e carregar novamente
        const existingScript = document.querySelector('script[src*="connect.facebook.net"]');
        if (existingScript) {
          existingScript.remove();
          window.FB = null;
        }
      }

      // Configurar fbAsyncInit
      window.fbAsyncInit = () => {
        initializeSDK();
      };

      // Criar e adicionar o script
      const script = document.createElement("script");
      script.async = true;
      script.defer = true;
      script.crossOrigin = "anonymous";
      script.src = "https://connect.facebook.net/pt_BR/sdk.js";
      script.onerror = () => {
        console.error("❌ Erro ao carregar script do Facebook SDK");
        setLoading(false);
        reject(new Error("Erro ao carregar script do Facebook SDK"));
      };
      document.body.appendChild(script);
    });
  }, [appId]);

  return { sdkLoaded, loading, loadSDK };
};

export default useFacebookSDK;
